import * as React from 'react';
declare const I18n;
import { MediaFile } from '../../../../types';
import CardItem from '../../components/card_item';
import Header from '../../../Header';
import BreadCrumb from '../../../BreadCrumb';
import moment from 'moment';
import api from "../../../../api";
import EventCalendar from '../../../Calendar/calendar';
import Pagination from '../../../Pagination/pagination';

type Event = {
  id: number;
  slug: string;
  title: string;
  date?: string;
  description: string;
  formatted_date?: string;
  cover_image_url?: string;
  target_role?: string;
  start_time?: string;
  end_time?: string;
  location?: string;
  categories?: Array<{id: number, name: string}>;
  language?: 'en' | 'fr';
}

type Props = {
  initialData: {
    events: Event[];
    image?: MediaFile;
    userType?: string;
    userLevel?: number;
    currentUserId?: number;
  }
}

type FilterType = 'all' | 'upcoming' | 'past';

const EventsIndex: React.FunctionComponent<Props> = ({
  initialData: {
    events,
    userType,
    userLevel,
    currentUserId,
  }
}: Props): JSX.Element => {
  const [eventList, setEventList] = React.useState(events);
  const [filter, setFilter] = React.useState<FilterType>('all');
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 6;
  const isAdmin = !(userType && (userType === 'Learner' || userType === 'Facilitator'));
  const isFacilitator = userType === 'Facilitator';
  const isLearner = userType === 'Learner';
    
  React.useEffect(() => {
    moment.locale(I18n.locale === 'fr' ? 'fr' : 'en');
  }, []);

  const formatCardDate = (rawDate?: string, fallbackDate?: string, language?: 'en' | 'fr') => {
    const locale = language === 'fr' ? 'fr' : 'en';
    const baseDate = rawDate ? moment(rawDate).locale(locale).format('LL') : (fallbackDate || '');

    if (locale !== 'fr') return baseDate;

    // Example: "27 mars 2026" -> "27 Mars 2026"
    return baseDate.replace(
      /(^|\s)([a-zàâçéèêëîïôûùüÿñæœ])/u,
      (_match, prefix, letter) => `${prefix}${letter.toUpperCase()}`
    );
  };

  const handleDelete = (id: number) => {
    const event = eventList.find((e) => e.id === id);
    if (!event) return;
    
    if (confirm(I18n.t('views.forms.messaging.confirm_delete'))) {
      const deleteUrl = isAdmin ? api.deleteEvent(event.slug) : api.deleteEvent(event.slug);
      
      deleteUrl
        .then((res) => {
          if (!res.error) {
            setEventList(eventList.filter((item) => item.id !== id));
          }
          window.location.href = res.url;
        })
        .catch((error) => console.error('Error', error));
    }
  }

  const getFilteredEvents = () => {
    const now = moment();
    
    switch (filter) {
      case 'upcoming':
        return eventList.filter(event => 
          moment(event.date || event.formatted_date).isSameOrAfter(now, 'day')
        );
      case 'past':
        return eventList.filter(event => 
          moment(event.date || event.formatted_date).isBefore(now, 'day')
        );
      case 'all':
      default:
        return eventList;
    }
  };

  const filteredEvents = getFilteredEvents();
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredEvents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEvents = filteredEvents.slice(startIndex, endIndex);

  // Reset to page 1 when filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  const calendarEvents = eventList.map((event, index) => ({
    id: event.id,
    title: event.title,
    start: moment(event.date || event.formatted_date).toDate(),
    end: moment(event.date || event.formatted_date).toDate(),
    resource: event,
    colorIndex: index % 3
  }));

  const handleSelectEvent = (event: any) => {
    const baseUrl = isAdmin ? '/admin' : '';
    window.location.href = `${baseUrl}/events/${event.resource.slug}`;
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of events list
    document.querySelector('.events_index__Box')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Determine the create button URL based on user type
  const getCreateButtonUrl = () => {
    if (isAdmin) return '/admin/events/new';
    if (isFacilitator) return '/events/new';
    return null;
  };

  return (
    <div className="events_index">
      <Header
        title={I18n.t('models.event.events')}
        titleButton={I18n.t('models.event.messaging.create')}
        locationButton={getCreateButtonUrl()}
        buttonDisabled={isLearner}
      />
      
      <EventCalendar 
        events={calendarEvents}
        onSelectEvent={handleSelectEvent}
      />

      <div className="events_index__Box">
        <div className="events_index__header">
          <h2>{ I18n.t('models.event.browse')}</h2>
          
          <div className="events_index__filters">
            <button
              className={`filter-button ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
             {I18n.t('models.event.filter.all')}
            </button>
            <button
              className={`filter-button ${filter === 'upcoming' ? 'active' : ''}`}
              onClick={() => setFilter('upcoming')}
            >
             {I18n.t('models.event.filter.upcoming')}
            </button>
            <button
              className={`filter-button ${filter === 'past' ? 'active' : ''}`}
              onClick={() => setFilter('past')}
            >
             {I18n.t('models.event.filter.past')}
            </button>
          </div>
        </div>
        
        <div className="courses_index__list" style={{ marginBottom: "1.25rem" }}>
          {
            paginatedEvents.length > 0 ? (
              <>
                {
                  paginatedEvents.map((row, index) => (
                    <CardItem
                      cardBackground='primary'
                      title={row.title}
                      description={row.description}
                      targetRole={row.target_role}
                      id={row.id}
                      index={index}
                      key={row.id}
                      image={row.cover_image_url ? row.cover_image_url : null}
                      handleDelete={handleDelete}
                      isFacilitator={isFacilitator}
                      isLearner={isLearner}
                      date={formatCardDate(row.date, row.formatted_date, (row as any).language)}
                      slug={row.slug}
                      start_time={row.start_time}
                      end_time={row.end_time}
                      location={row.location}
                      categories={row.categories}
                      user_id={row.user_id}
                      current_user_id={currentUserId}
                    />
                  ))
                }
              </>
            ) : (
              <div className="courses_index__empty-state app-text">
                {I18n.t('models.event.messaging.empty_state')}
              </div>
            )
          }
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
}

export default EventsIndex;